import React, { useState, useEffect } from 'react';
import { User, Lock, Bell, CheckCircle, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AccountSettings = () => {
    const [activeTab, setActiveTab] = useState('security');
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const navigate = useNavigate();

    // Password State
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const handlePasswordUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');
        setSuccessMsg('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/update-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ currentPassword, newPassword })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to update password');
            }

            setSuccessMsg('Password updated successfully!');
            setCurrentPassword('');
            setNewPassword('');
        } catch (err: any) {
            setErrorMsg(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            {/* Simple Header for Settings Page */}
            <div className="bg-[#011E26] text-white py-6 px-8 shadow-md">
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <h1 className="text-2xl font-serif font-bold">Account Settings</h1>
                    <button onClick={() => navigate('/student/dashboard')} className="text-sm text-[#009B8F] hover:text-white transition-colors">
                        &larr; Back to Dashboard
                    </button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 py-8">
                <div className="flex flex-col md:flex-row gap-8">

                    {/* Sidebar */}
                    <div className="w-full md:w-64 flex-shrink-0">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <button
                                onClick={() => setActiveTab('general')}
                                className={`w-full text-left px-6 py-4 flex items-center gap-3 text-sm font-bold transition-colors ${activeTab === 'general' ? 'bg-[#009B8F]/10 text-[#009B8F] border-l-4 border-[#009B8F]' : 'text-slate-600 hover:bg-gray-50'}`}
                            >
                                <User size={18} /> General
                            </button>
                            <button
                                onClick={() => setActiveTab('security')}
                                className={`w-full text-left px-6 py-4 flex items-center gap-3 text-sm font-bold transition-colors ${activeTab === 'security' ? 'bg-[#009B8F]/10 text-[#009B8F] border-l-4 border-[#009B8F]' : 'text-slate-600 hover:bg-gray-50'}`}
                            >
                                <Lock size={18} /> Security
                            </button>
                            <button
                                onClick={() => setActiveTab('notifications')}
                                className={`w-full text-left px-6 py-4 flex items-center gap-3 text-sm font-bold transition-colors ${activeTab === 'notifications' ? 'bg-[#009B8F]/10 text-[#009B8F] border-l-4 border-[#009B8F]' : 'text-slate-600 hover:bg-gray-50'}`}
                            >
                                <Bell size={18} /> Notifications
                            </button>
                            <button
                                onClick={() => setActiveTab('linked')}
                                className={`w-full text-left px-6 py-4 flex items-center gap-3 text-sm font-bold transition-colors ${activeTab === 'linked' ? 'bg-[#009B8F]/10 text-[#009B8F] border-l-4 border-[#009B8F]' : 'text-slate-600 hover:bg-gray-50'}`}
                            >
                                <Shield size={18} /> Linked Accounts
                            </button>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1">
                        {activeTab === 'security' && (
                            <div className="space-y-6">
                                {successMsg && (
                                    <div className="bg-green-100 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm font-bold shadow-sm">
                                        <CheckCircle size={16} /> {successMsg}
                                    </div>
                                )}
                                {errorMsg && (
                                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm text-center">
                                        {errorMsg}
                                    </div>
                                )}

                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                                    <h2 className="text-xl font-bold text-[#011E26] mb-6">Password Management</h2>
                                    <form onSubmit={handlePasswordUpdate} className="max-w-md space-y-6">
                                        <div>
                                            <input
                                                type="password"
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                placeholder="Current Password"
                                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#009B8F] transition-all text-sm"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="New Password"
                                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#009B8F] transition-all text-sm"
                                                required
                                                minLength={6}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-[#009B8F] hover:bg-[#00857a] text-white font-bold py-4 rounded-lg transition-all shadow-lg shadow-[#009B8F]/20 disabled:opacity-70"
                                        >
                                            {loading ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </form>
                                </div>

                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 opacity-60 relative">
                                    <div className="absolute top-4 right-4 bg-gray-200 text-gray-600 text-xs font-bold px-2 py-1 rounded">Off</div>
                                    <h2 className="text-xl font-bold text-[#011E26] mb-2">Two-Factor Authentication</h2>
                                    <p className="text-slate-500 text-sm mb-4">Add an extra layer to security to your account.</p>
                                    <div className="flex items-center gap-2">
                                        <div className="w-10 h-6 bg-gray-300 rounded-full relative cursor-not-allowed">
                                            <div className="w-4 h-4 bg-white rounded-full absolute left-1 top-1"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'general' && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
                                <User size={48} className="text-slate-200 mb-4" />
                                <h3 className="text-lg font-bold text-slate-700">General Profile Settings</h3>
                                <p className="text-slate-500 text-sm mt-2">You can update your basic profile details in "My Profile".</p>
                                <button onClick={() => navigate('/student/profile')} className="mt-4 text-[#009B8F] font-bold text-sm hover:underline">Go to My Profile</button>
                            </div>
                        )}

                        {activeTab === 'notifications' && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 min-h-[300px]">
                                <h3 className="text-lg font-bold text-[#011E26] mb-6">Notification Preferences</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-bold text-sm text-[#011E26]">Email Notifications</p>
                                            <p className="text-xs text-slate-500">Receive updates about your application status.</p>
                                        </div>
                                        <div className="w-10 h-6 bg-[#009B8F] rounded-full relative cursor-pointer">
                                            <div className="w-4 h-4 bg-white rounded-full absolute right-1 top-1"></div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-bold text-sm text-[#011E26]">Marketing Emails</p>
                                            <p className="text-xs text-slate-500">Receive news and promotional offers.</p>
                                        </div>
                                        <div className="w-10 h-6 bg-gray-300 rounded-full relative cursor-pointer">
                                            <div className="w-4 h-4 bg-white rounded-full absolute left-1 top-1"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'linked' && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
                                <Shield size={48} className="text-slate-200 mb-4" />
                                <h3 className="text-lg font-bold text-slate-700">Linked Accounts</h3>
                                <p className="text-slate-500 text-sm mt-2">Manage Google or Social logins here.</p>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountSettings;
